import { CircleText, QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Spot } from '@doltech/ui/lib/figma/QuestionWithSpots/types';
import { deviceBreakpoints, useDeviceDetect } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { numberToABC } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { DoTestDragInInstance } from '../../../student-do-test/model/Question/do-test-drag-in.store';
import { scaleDraggingPoint } from '../../../student-test-result/mobile/components/DragOutQuestionResultMobile';

interface DragInProps extends React.HTMLAttributes<HTMLDivElement> {
  data: DoTestDragInInstance;
  isPdfContent: boolean;
}

const Main = styled.div.withConfig({
  componentId: 'DragInAnswerSubject_Main',
} as any)`
  .bg-container {
    display: grid;
    gap: 24px;
  }

  .spot-item-container {
    position: absolute;
    display: flex;
    align-items: center;
    z-index: 1;

    .spot-item {
      width: 24px;
      height: 24px;
      display: grid;
      place-items: center;
      border-radius: 50%;
      background-color: ${colorsV2.gray100};
      color: ${colorsV2.white100};
      font-size: 10px;
      font-weight: 600;
      text-align: center;
    }
    .input {
      margin-left: 8px;
      min-width: 53px;
      height: 24px;
      border-bottom: 2px solid ${colorsV2.gray50};
    }
  }

  .question-option-view-subject {
    margin-right: 8px;
    margin-bottom: 8px;
  }

  .two-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    grid-auto-rows: max-content;
    .option-list {
      flex: 1;
    }
    .question-option-drag.shape-circle.question-circle {
      background-color: ${colorsV2.black80};
    }
  }

  &.drag-in-answer-subject {
    .spot-item-container {
      padding: 6px 12px;
    }
  }

  &.drag-out-answer-subject {
    .spot-item-container {
      .spot-border {
        min-width: 30px;
        min-height: 30px;
        max-width: 30px;
        max-height: 30px;
        background-color: ${colorsV2.gray10};
        border-radius: 50%;
        display: grid;
        place-items: center;
        .spot-item {
          min-width: 22px;
          min-height: 22px;
          max-width: 22px;
          max-height: 22px;
          background-color: ${colorsV2.black80};
          line-height: 0;
        }
      }
    }
    .question-list {
      display: grid;
      gap: 20px;
      height: max-content;
      .spot-item-container {
        position: initial;
        .input {
          width: 88px;
        }
        .spot-item {
          margin-left: 8px;
          background-color: ${colorsV2.black100};
        }
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  @media ${deviceBreakpoints.fromTablet} {
    .bg-container {
      grid-template-columns: 376px auto;
    }
  }
`;

const ImgBorder = styled.div.withConfig({
  componentId: 'DragInAnswerSubject_ImgBorder',
} as any)`
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  width: fit-content;
`;

const Img = styled.img.withConfig({
  componentId: 'DragInAnswerSubject_Img',
} as any)`
  width: 100%;
  max-width: 100%;
  height: 100%;
`;

interface SharedDragInOutProps extends Omit<DragInProps, 'data'> {
  data: any;
  renderQuestionInsideImage: ({
    getSpotCoordinates,
    isMobile,
  }: {
    getSpotCoordinates: (question: any) => { top: number; left: number };
    isMobile: boolean;
  }) => JSX.Element | React.ReactNode | React.ReactNode[];
  renderQuestionList: () => JSX.Element | React.ReactNode | React.ReactNode[];
}

export const SharedDragInOut = observer((props: SharedDragInOutProps) => {
  const { data, isPdfContent, renderQuestionInsideImage, renderQuestionList, className, ...rest } =
    props;
  const { image } = data;
  const { width, height, url, name } = image;
  const [scaledImageWidth, setScaledImageWidth] = React.useState(width);
  const [scaledImageHeight, setScaledImageHeight] = React.useState(height);
  const imgElement = React.useRef(null);
  const { isMobile } = useDeviceDetect();

  const getSpotCoordinates = React.useCallback(
    (question) => {
      return {
        top: scaleDraggingPoint(height, scaledImageHeight, question.y),
        left: scaleDraggingPoint(width, scaledImageWidth, question.x),
      };
    },
    [height, scaledImageHeight, scaledImageWidth, width]
  );

  React.useEffect(() => {
    if (imgElement?.current) {
      if (isPdfContent) {
        setScaledImageWidth(width);
        setScaledImageHeight(height);
      } else {
        setScaledImageWidth(imgElement.current.clientWidth || width);
        setScaledImageHeight(imgElement.current.clientHeight || height);
      }
    }
  }, [height, isMobile, isPdfContent, width]);

  return (
    <Main className={cl('dol-question', className)} {...rest}>
      <div className="bg-container">
        <ImgBorder key={url}>
          <Img
            loading="lazy"
            ref={imgElement}
            onLoad={() => {
              if (isPdfContent) {
                setScaledImageWidth(width);
                setScaledImageHeight(height);
              } else {
                setScaledImageWidth(imgElement.current.clientWidth);
                setScaledImageHeight(imgElement.current.clientHeight);
              }
            }}
            key={url}
            src={url}
            alt={name}
          />
          {renderQuestionInsideImage({ getSpotCoordinates, isMobile })}
        </ImgBorder>
        {renderQuestionList()}
      </div>
    </Main>
  );
});

const DragInQuestionCircle = styled.div.withConfig({
  componentId: 'DragInAnswerSubject_DragInQuestionCircle',
} as any)``;

export const DragInAnswerSubject = observer((props: DragInProps) => {
  const { data } = props;
  const { options, questions, no } = data;

  return (
    <SharedDragInOut
      {...props}
      className="drag-in-answer-subject"
      renderQuestionInsideImage={({ getSpotCoordinates, isMobile }) => {
        return questions.map((question: Spot, idx: number) => {
          const order = idx + no;
          const coordinates = getSpotCoordinates(question);
          const style = {
            top: coordinates.top,
            left: coordinates.left,
          };
          return (
            <DragInQuestionCircle key={question.id} style={style} className="spot-item-container">
              <div className="spot-item">{order}</div>
              <div className="input" />
            </DragInQuestionCircle>
          );
        });
      }}
      renderQuestionList={() => {
        let index = 0;
        return (
          <div className="two-row">
            {options.map((option) => {
              index += 1;
              return (
                <CircleText
                  key={option}
                  displayMode={QuestionDisplayMode.QUESTION_OPTION_DRAG}
                  circleText={numberToABC(index)}
                  text={option}
                />
              );
            })}
          </div>
        );
      }}
    />
  );
});
